import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Checkbox, Modal, Progress, Typography } from 'antd';

import {
	EXPORT_ROW_CAP,
	buildExportFilename,
	downloadBlob,
	fetchExport,
	linkedOmittedNote,
} from '../../services/exports';
import ExportOptionsFields, { buildStoredOptions, exportCapFor } from './ExportOptionsFields';
import { DOWNLOAD, FILE_XLSX } from '../icons';
import QueryRefusalPick from '../items/QueryRefusalPick';
import { Spinner, useDelayedBusy } from '../loading';
import { elementRefusalOf, withElementChoice, withElementChoicesParam } from '../../utils/elementRefusal';
import { anchorOf } from '../../platform';

const { Text } = Typography;

/**
 * An element refusal behind a failed export (element ids step 3): the export
 * is fetched as a blob, so a 400's body arrives as one and is read here.
 * null for any other failure.
 */
async function elementRefusalOfBlobError(error) {
	if (error?.response?.status !== 400) return null;
	const data = error.response.data;
	try {
		const body = data instanceof Blob ? JSON.parse(await readBlobText(data)) : data;
		return elementRefusalOf(body);
	} catch (parseError) {
		return null;
	}
}

// jsdom (and older Safari) have no Blob.text(); FileReader is the floor.
const readBlobText = (blob) => {
	if (typeof blob?.text === 'function') return blob.text();
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(`${reader.result || ''}`);
		reader.onerror = () => reject(reader.error || new Error('Unable to read the export.'));
		reader.readAsText(blob);
	});
};

/**
 * One export dialog for every exportable thing (item, filtered item list,
 * card definition, collection): pick a format, see the document, download it.
 *
 * The preview IS the file — the same fetch that renders the PDF iframe or the
 * pretty-printed JSON is reused by the Download button, so what the user saw
 * is what they save. Excel is the exception: browsers can't render it, so the
 * modal says so and Download fetches on demand.
 *
 * `target` is a services/exports.js target:
 *   { kind: 'item', itemId } | { kind: 'items', params }
 *   | { kind: 'card', cardId } | { kind: 'collection', collectionId }
 *   | { kind: 'tag', tagId, params } (tags T9: the tag page's rows — a list)
 *
 * **Include my private tags** (tags T9, ruled d08) — off by default, offered
 * on an item, a list and a tag page wherever the file prints tags: an item's
 * PDF and JSON, a list's Excel, JSON and full item pages (a table or grid PDF
 * prints no tags, so the question is not asked there: a choice the file would
 * not show is not offered). The person's own private tags land in a column
 * or row of their own. It is THIS dialog's question, never
 * `ExportOptionsFields`' — the stored-export editor shares those, and an
 * export definition never carries private tags (ruled D8b); **Save this
 * export…** leaves it out (buildStoredOptions never writes it).
 *
 * `supportsExcel` — kind 'items' only (the API renders xlsx for lists alone).
 * `supportsAttachmentOptions` — kind 'item' only (single-item PDFs can list
 * and embed the item's attachments).
 * `listLayout` — kind 'items' only: the view mode ('grid'|'table') active on
 * the items page, so a PDF of "List (current view)" is laid out the way the
 * screen is. A list PDF can instead render "Full item pages"
 * (layout=records): every item as its own item-form PDF in one sequential
 * document, which is when the attachment options apply to a list too.
 *
 * The questions themselves are `ExportOptionsFields`, shared with the stored
 * export editor. `onSaveAsScheduled`, when given, adds **Save this export…**:
 * it hands back the format and the options on screen in a stored
 * definition's spelling (`buildStoredOptions`), so the caller can open the
 * editor on the document the person is already looking at. Only a caller
 * that knows where such a definition would live — a collection a manager
 * can configure — passes it.
 */
export default function ExportModal({
	open,
	onClose,
	target,
	title = 'Export',
	baseName,
	supportsExcel = false,
	supportsAttachmentOptions = false,
	listLayout = 'grid',
	// The report preset the list is filtered by, when it has one: a saved
	// filter that also says how to lay its results out.
	report = null,
	onSaveAsScheduled = null,
	// A card in the export's scope holds a repeating group (KPR-685): Excel
	// then asks one line per item or one line per entry.
	offersGroupShape = false,
}) {
	const { message } = App.useApp();
	// A tag page exports like a list (its rows cross cards and collections).
	const isItemsTarget = target?.kind === 'items' || target?.kind === 'tag';
	const offersPrivateTags = ['item', 'items', 'tag'].includes(target?.kind);

	const [format, setFormat] = useState('pdf');
	// Items-list PDFs only: 'list' (the current view, as a table/grid) or
	// 'records' (every item as its own full page).
	const [contents, setContents] = useState('list');
	const [attachmentList, setAttachmentList] = useState(true);
	const [embedAttachments, setEmbedAttachments] = useState(false);
	const [privateTags, setPrivateTags] = useState(false);
	// Excel: a repeating group one line per item ('item') or per entry.
	const [groupShape, setGroupShape] = useState('item');

	const [objectUrl, setObjectUrl] = useState(null);
	const [jsonText, setJsonText] = useState(null);
	const [truncated, setTruncated] = useState(false);
	const [linkedOmitted, setLinkedOmitted] = useState(0);
	const [totalCount, setTotalCount] = useState(null);
	const [loading, setLoading] = useState(false);
	// How much of the export has arrived (0 to 1), or null when the response does not say how long it is.
	const [progress, setProgress] = useState(null);
	const [failed, setFailed] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const { visible: showBusy } = useDelayedBusy(loading || downloading);
	// Bumped by the error state's Retry button to re-run the fetch effect.
	const [attempt, setAttempt] = useState(0);

	// The already-previewed bytes, reused by Download so the saved file is the
	// one on screen (null for Excel, which is fetched on demand).
	const blobRef = useRef(null);

	// The target is an object prop; identity churn from a parent re-render must
	// not refetch, so the effect keys on its VALUE and reads it through a ref.
	const targetRef = useRef(target);
	targetRef.current = target;
	const targetKey = JSON.stringify(target || null);

	// Each opening starts from the defaults — a modal reopened for another
	// record must not inherit the last session's format or checkboxes.
	useEffect(() => {
		// "Include my private tags" also goes off as the dialog closes: the
		// reset below lands a render after the next opening's first fetch,
		// which must not carry it (T9, off every time the dialog opens).
		if (!open) {
			setPrivateTags(false);
			return;
		}
		setFormat('pdf');
		setContents('list');
		setAttachmentList(true);
		setEmbedAttachments(false);
		setPrivateTags(false);
		setGroupShape('item');
	}, [open]);

	const [ blankForm, setBlankForm ] = useState(false);

	// Does the file this configuration builds print tags at all? Only then is
	// "Include my private tags" asked (and sent).
	const printsTags = offersPrivateTags && (
		format === 'json'
		|| format === 'xlsx'
		|| (format === 'pdf' && (!isItemsTarget || contents === 'records'))
	);
	const sendPrivateTags = printsTags && privateTags;

	const buildOptions = useCallback((forFormat) => {
		const tagOption = sendPrivateTags ? { privateTags: true } : {};
		if (forFormat === 'xlsx' && isItemsTarget && offersGroupShape && groupShape === 'entries') return { ...tagOption, groups: 'entries' };
		if (forFormat !== 'pdf') return tagOption;
		const options = { ...tagOption };
		if (isItemsTarget) {
			// 'list' is whichever view the user is looking at; the server's
			// default (table) is never relied on because the screen decides.
			options.layout = contents === 'records'
				? 'records'
				: contents === 'matrix'
					? 'matrix'
					: (listLayout === 'table' ? 'table' : 'grid');
			// The matrix is a PIVOT: rows down, period buckets across, one
			// mark per cell (a medication record, a timesheet, an attendance
			// roll). Its shape comes from the report preset the list is
			// filtered by, so the modal passes it through rather than asking
			// the same five questions again here.
			if (contents === 'matrix' && report) {
				options.matrixRows = report.rows;
				options.matrixField = report.field;
				options.matrixBucket = report.bucket;
				if (report.group) options.matrixGroup = report.group;
				if (report.cell) options.matrixCell = report.cell;
				if (blankForm) options.matrixBlank = true;
			}
		}
		if (supportsAttachmentOptions || (isItemsTarget && contents === 'records')) {
			options.attachmentList = attachmentList;
			options.attachments = embedAttachments;
		}
		return options;
	}, [isItemsTarget, contents, listLayout, supportsAttachmentOptions, attachmentList, embedAttachments, report, blankForm, sendPrivateTags, offersGroupShape, groupShape]);
	const buildOptionsRef = useRef(buildOptions);
	buildOptionsRef.current = buildOptions;

	// An element the list's filter could mean several of (400
	// `ambiguous_element`, element ids step 3), and the person's picks: sent
	// with the export's `q` as `elementChoices`, as the list sends them.
	const [elementRefusal, setElementRefusal] = useState(null);
	const [exportChoices, setExportChoices] = useState(null);
	useEffect(() => { setElementRefusal(null); setExportChoices(null); }, [open, targetKey]);
	const exportTarget = useCallback(() => {
		const target = targetRef.current;
		if (!target || !exportChoices || !target.params?.q) return target;
		return { ...target, params: withElementChoicesParam(target.params, exportChoices) };
	}, [exportChoices]);

	// Fetch the preview on open and on every format/option change, resetting
	// first: never show one export's bytes under another's settings. Excel is
	// skipped entirely — nothing can render it, so nothing is fetched.
	useEffect(() => {
		setObjectUrl(null);
		setJsonText(null);
		setTruncated(false);
		setLinkedOmitted(0);
		setTotalCount(null);
		setFailed(false);
		setLoading(false);
		blobRef.current = null;

		if (!open || !targetRef.current || format === 'xlsx') return undefined;

		let active = true;
		let created = null;
		setLoading(true);
		setProgress(null);

		fetchExport(exportTarget(), format, { ...buildOptionsRef.current(format), onProgress: setProgress })
			.then(async ({ blob, truncated: wasTruncated, totalCount: total, linkedOmitted: omitted = 0 }) => {
				if (!active) return;
				setTruncated(wasTruncated);
				setLinkedOmitted(omitted);
				setTotalCount(total);
				blobRef.current = blob;
				if (format === 'json') {
					const raw = await readBlobText(blob);
					if (!active) return;
					let pretty = raw;
					try {
						pretty = JSON.stringify(JSON.parse(raw), null, 2);
					} catch (parseError) {
						// Show what came back rather than nothing; the download
						// still saves the exact server bytes.
					}
					setJsonText(pretty);
					return;
				}
				created = URL.createObjectURL(blob);
				if (!active) {
					URL.revokeObjectURL(created);
					created = null;
					return;
				}
				setObjectUrl(created);
			})
			.catch(async (error) => {
				const refusal = await elementRefusalOfBlobError(error);
				if (!active) return;
				if (refusal) { setElementRefusal(refusal); setFailed(true); return; }
				console.error(error);
				setFailed(true);
			})
			.finally(() => {
				if (active) setLoading(false);
			});

		return () => {
			active = false;
			// A one-shot handle: closing or changing settings revokes it, and
			// the next look fetches again.
			if (created) URL.revokeObjectURL(created);
		};
	}, [open, targetKey, format, contents, listLayout, attachmentList, embedAttachments, sendPrivateTags, attempt, exportTarget]);

	// The cap that applies to what is currently configured — worded up front
	// so nobody is surprised by a shorter file, and reused by the truncation
	// notes the response headers drive.
	const capInPlay = exportCapFor({ format, contents, embedAttachments, isItemsTarget });

	// A measurable wait is a determinate bar, an unmeasurable one a spinner (§14).
	const busyIndicator = label => (progress != null
		? <Progress className='export-modal__progress' percent={Math.round(progress * 100)} size='small' aria-label={label} />
		: <Spinner label={label} showLabel />);

	// `shown`: the items the file holds when the server says (Excel one line
	// per entry stops at its line cap, whole items only); else the cap.
	const truncationNote = useCallback((total, shown = null) => `Export limited to the first ${
		(shown ?? capInPlay ?? EXPORT_ROW_CAP).toLocaleString()
	} items${total != null ? ` (${total.toLocaleString()} total)` : ''}.`, [capInPlay]);

	const handleDownload = useCallback(async (event) => {
		const anchor = anchorOf(event);
		if (!targetRef.current) return;
		setDownloading(true);
		setProgress(null);
		try {
			let blob = blobRef.current;
			if (!blob) {
				// Excel (never previewed), or a preview that hasn't landed. This
				// path never renders the truncation note, so say it out loud.
				const result = await fetchExport(
					exportTarget(),
					format,
					{ ...buildOptionsRef.current(format), onProgress: setProgress }
				);
				blob = result.blob;
				if (result.truncated) message.info(truncationNote(result.totalCount, result.itemCount));
				if (result.linkedOmitted) message.info(linkedOmittedNote(result.linkedOmitted));
			}
			await downloadBlob(blob, buildExportFilename(targetRef.current, format, baseName), { anchor });
		} catch (error) {
			const refusal = await elementRefusalOfBlobError(error);
			if (refusal) {
				setElementRefusal(refusal);
			} else {
				console.error(error);
				message.error('Unable to export. Please try again.');
			}
		} finally {
			setDownloading(false);
		}
	}, [format, baseName, message, truncationNote, exportTarget]);

	// Never the private tags: an export definition cannot carry them (D8b).
	const handleSave = () => onSaveAsScheduled({
		format,
		options: buildStoredOptions({
			format,
			contents,
			listLayout,
			attachmentList,
			embedAttachments,
			blankForm,
			isItemsTarget,
			supportsAttachmentOptions,
			report,
			groupShape: offersGroupShape ? groupShape : 'item',
		}),
	});

	const renderBody = () => {
		// The list's filter names an element it could mean several of: the
		// person picks, and the export is asked again with the pick.
		if (elementRefusal) {
			return (
				<QueryRefusalPick
					inline
					refusal={elementRefusal}
					onPick={(value) => {
						setExportChoices(prev => withElementChoice(prev || {}, elementRefusal, value));
						setElementRefusal(null);
					}}
				/>
			);
		}
		if (format === 'xlsx') {
			return (
				<div className='attachment-preview__unsupported export-modal__no-preview'>
					<span className='attachment-preview__unsupported-icon' aria-hidden='true'>
						<FILE_XLSX />
					</span>
					<Text type='secondary'>Preview isn't available for Excel — download to open.</Text>
					{downloading && showBusy ? busyIndicator('Downloading the export') : null}
				</div>
			);
		}
		if (loading) {
			// Nothing for the first 150 ms, then a bar if the response says how
			// long it is and a spinner if it does not; the frame is the same size
			// either way.
			return (
				<div className='export-modal__loading'>
					{showBusy ? busyIndicator('Building the export') : null}
				</div>
			);
		}
		if (failed) {
			return (
				<Alert
					type='error'
					showIcon
					message='Unable to build this export.'
					action={(
						<Button size='small' onClick={() => setAttempt(prev => prev + 1)}>
							Retry
						</Button>
					)}
				/>
			);
		}
		if (format === 'pdf' && objectUrl) {
			return <iframe className='attachment-preview__frame' title={title} src={objectUrl} />;
		}
		if (format === 'json' && jsonText !== null) {
			// A TEXT NODE, never markup: exported data is shown, not interpreted.
			return <pre className='attachment-preview__text'>{jsonText}</pre>;
		}
		return null;
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			width={720}
			centered
			destroyOnClose
			className='export-modal'
			title={title}
			footer={[
				<Button key='close' onClick={onClose}>Close</Button>,
				...(typeof onSaveAsScheduled === 'function' ? [
					<Button key='save' onClick={handleSave}>Save this export…</Button>,
				] : []),
				<Button
					key='download'
					type='primary'
					icon={<DOWNLOAD />}
					loading={downloading}
					disabled={loading}
					onClick={handleDownload}
				>
					Download
				</Button>,
			]}
		>
			<div className='export-modal__controls'>
				<ExportOptionsFields
					format={format}
					onFormatChange={setFormat}
					contents={contents}
					onContentsChange={setContents}
					attachmentList={attachmentList}
					onAttachmentListChange={setAttachmentList}
					embedAttachments={embedAttachments}
					onEmbedAttachmentsChange={setEmbedAttachments}
					blankForm={blankForm}
					onBlankFormChange={setBlankForm}
					groupShape={groupShape}
					onGroupShapeChange={setGroupShape}
					offersGroupShape={offersGroupShape}
					supportsExcel={supportsExcel}
					supportsAttachmentOptions={supportsAttachmentOptions}
					isItemsTarget={isItemsTarget}
					report={report}
				/>
				{printsTags ? (
					<div className="export-modal__options export-modal__private-tags">
						<div className="export-modal__option">
							<Checkbox
								checked={privateTags}
								onChange={event => setPrivateTags(event.target.checked)}
								data-testid="export-private-tags"
							>
								Include my private tags
							</Checkbox>
							<Text type="secondary" className="export-modal__hint">
								Only you see these. In the file they’re marked “My private tags”.
							</Text>
						</div>
					</div>
				) : null}
			</div>
			{truncated ? (
				<Text type='secondary' className='attachment-preview__note'>
					{truncationNote(totalCount)}
				</Text>
			) : null}
			{linkedOmitted ? (
				<Text type='secondary' className='attachment-preview__note' data-testid='export-linked-omitted'>
					{linkedOmittedNote(linkedOmitted)}
				</Text>
			) : null}
			{renderBody()}
		</Modal>
	);
}
